// src/pages/VieClub/VieClubCard.jsx
import React, { useState, useEffect } from "react";
import Slider from "react-slick";
import Button from "../../components/Button/Button";
import { deleteArticle } from "../../services/vieclubServices";
import { getUserById } from "../../services/userServices";
import { toast } from "react-toastify";
import { FaTrashAlt, FaEdit } from "react-icons/fa";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const VieClubCard = ({
  article,
  index,
  userRole,
  refreshArticlesList,
  user,
  onEdit,
}) => {
  const defaultImage = "/img/default_image.jpg";
  const [author, setAuthor] = useState(null);
  const [selectedImg, setSelectedImg] = useState(null);

  const images = [
    article.photo_url1,
    article.photo_url2,
    article.photo_url3,
  ].filter(Boolean);
  const imageCount = images.length;

  const settings = {
    dots: imageCount > 1,
    infinite: imageCount > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: imageCount > 1,
    autoplaySpeed: 3000,
    arrows: imageCount > 1,
  };

  useEffect(() => {
    const fetchAuthor = async () => {
      if (article.user_id) {
        const authorData = await getUserById(article.user_id);
        setAuthor(authorData);
      }
    };
    fetchAuthor();
  }, [article.user_id]);

  const handleDeleteArticle = async () => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cet article ?")) {
      try {
        await deleteArticle(article.id);
        toast.success("Article supprimé avec succès.");
        refreshArticlesList();
      } catch (error) {
        console.error("Erreur lors de la suppression de l'article:", error);
        toast.error("Erreur lors de la suppression de l'article.");
      }
    }
  };

  const isReversed = index % 2 === 1;

  const canEditOrDelete =
    user?.userId === article.user_id || [1, 2, 5, 7].includes(userRole);

  const formatUrl = (url) => {
    if (!url) return "";
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      return `http://${url}`;
    }
    return url;
  };

  const handleClickImage = (img) => setSelectedImg(img);

  return (
    <div className="relative w-full">
      {/* Badge statut */}
      <div
        className={`absolute z-10 top-3 right-3 px-3 py-1 rounded-full text-[10px] font-semibold tracking-wide ${
          article.is_active
            ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
            : "bg-amber-100 text-amber-700 border border-amber-200"
        }`}
      >
        {article.is_active ? "Publié" : "Masqué"}
      </div>

      <div
        className={`flex flex-col ${
          isReversed ? "md:flex-row-reverse" : "md:flex-row"
        } shadow-lg bg-white/95 dark:bg-slate-900/95 border border-slate-200/80 dark:border-slate-700/80 rounded-2xl overflow-hidden transition-all duration-150 hover:-translate-y-1 hover:shadow-xl`}
      >
        {/* Images */}
        <div className="md:w-1/2 w-full bg-slate-900/5 dark:bg-black/30">
          {imageCount > 0 ? (
            <Slider {...settings}>
              {images.map((img, i) => (
                <div
                  key={i}
                  className="w-full h-full"
                  onClick={() => handleClickImage(img)}
                >
                  <img
                    src={img}
                    alt={`Illustration ${i + 1}`}
                    className="object-cover w-full h-72 sm:h-80 cursor-pointer"
                  />
                </div>
              ))}
            </Slider>
          ) : (
            <img
              src={defaultImage}
              alt="Default article cover"
              className="object-cover w-full h-72 sm:h-80"
            />
          )}
        </div>

        {/* Contenu */}
        <div className="md:w-1/2 p-4 sm:p-6 flex flex-col justify-between">
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-slate-800 dark:text-white mb-1">
              {article.title}
            </h3>
            {article.club && (
              <p className="text-xs uppercase tracking-[0.18em] text-orange-500 font-semibold mb-3">
                {article.club}
              </p>
            )}

            {author && (
              <div className="flex items-center gap-3 mb-4">
                <img
                  src={author.photoURL || defaultImage}
                  alt={author.nom}
                  className="w-10 h-10 rounded-full object-cover border border-slate-200 dark:border-slate-600"
                />
                <div className="text-slate-600 dark:text-slate-200 text-sm">
                  <p className="font-semibold">
                    {author.nom} {author.prenom}
                  </p>
                  <p className="text-[11px] opacity-80">
                    Auteur de l&apos;article
                  </p>
                </div>
              </div>
            )}

            <div
              className="text-sm text-slate-700 dark:text-slate-200 mb-4 prose max-w-none prose-sm dark:prose-invert"
              dangerouslySetInnerHTML={{ __html: article.content }}
            ></div>

            <p className="text-[11px] text-slate-500 dark:text-slate-300">
              Publié le{" "}
              {new Date(article.date_article).toLocaleDateString("fr-FR")}
            </p>

            {article.lien_url && (
              <p className="mt-2 text-sm">
                <a
                  href={formatUrl(article.lien_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-primary underline underline-offset-2 hover:opacity-90"
                >
                  Consulter le lien associé
                </a>
              </p>
            )}
          </div>

          {canEditOrDelete && (
            <div className="flex justify-end gap-2 mt-4">
              <Button
                text=""
                icon={FaEdit}
                onClick={onEdit}
                type="button"
                className="btn-xs rounded-full btn-primary"
                hoverLabel="Modifier l'article"
              />
              <Button
                text=""
                icon={FaTrashAlt}
                onClick={handleDeleteArticle}
                type="button"
                className="btn-xs rounded-full btn-danger"
                hoverLabel="Supprimer l'article"
              />
            </div>
          )}
        </div>

        {/* Lightbox image */}
        {selectedImg && (
          <div
            className="fixed inset-0 bg-black/70 z-50 flex justify-center items-center"
            onClick={() => setSelectedImg(null)}
          >
            <img
              src={selectedImg}
              alt="Selected"
              className="max-w-[90%] max-h-[90%] rounded-xl shadow-2xl border border-slate-200/40"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default VieClubCard;
